import React from 'react';
import {Form} from 'react-form';
import {Link} from 'react-router-dom';
import {connect} from 'react-redux';
import async from 'async';
import _ from 'lodash';
import moment from 'moment';
import Translate from 'translate';
import { v4 as uuidv4 } from 'uuid';

import UserService from 'services/UserService';
import UserGroupService from 'services/UserGroupService';
import {FormGroup, Text, Select, Checkbox} from 'components/FormElements/';
import {toggleLoading, toggleAlertError} from 'stores/actions/';
import {email, equalTo, passwordValidation, required} from 'constants/InputErrors';

class UserEdit extends React.Component {
  constructor( props ) {
    super( props );

    this.state = {
      data: {}
    };

    this.entityId = props.match.params.id;
    this.submitForm = this.submitForm.bind( this );
  }

  componentDidMount() {
    this.getData()
  }

  getUser( cb ) {
    if( !this.entityId ) return cb( null, {} );
    UserService.getUser( this.entityId ).then( ( response ) => cb( response.errors, response.data ) )
  }

  getUserGroups( cb ) {
    UserGroupService.getUserGroups()
      .then( ( response ) => cb( response.errors, response.data ? response.data.data : [] ) )
  }

  getData() {
    this.props.toggleLoading( true );

    async.parallel( {
      data: this.getUser.bind( this ),
      userGroups: this.getUserGroups.bind( this ),
    }, ( error, results ) => {
      if( !error ) this.setState( results );
      this.props.toggleLoading( false );
    } );
  }

  submitForm( formData ) {
    const { history, toggleAlertError, toggleLoading, externalAuthenticationRequired } = this.props;
    this.formData = _.cloneDeep( formData );

    toggleLoading( true );

    const data = {
      name: formData.name,
      lastName: formData.lastname,
      dni: formData.dni,
      gender: formData.gender,
      email: formData.email,
      username: formData.username,
      password: this.entityId ? undefined : externalAuthenticationRequired || formData.externalAuthentication ? uuidv4() : formData.password,
      enabled: formData.enabled,
      externalAuthentication: formData.externalAuthentication,
      userGroup: _.isObject( formData.userGroup ) ? formData.userGroup.id : formData.userGroup,
      roles: _.isObject( formData.roles[0] ) ? _.map( formData.roles, 'id' ) : formData.roles,
    };
    const promise = this.entityId ? UserService.updateUser( this.entityId, data ) :
      UserService.saveUser( data );

    promise.then( ( response ) => {
      toggleLoading( false );
      if( response.errors ) {
        if( response.errors === 'fos_user.username.already_used' || response.errors === 'fos_user.email.already_used' )
          return toggleAlertError( <Translate id={response.errors}/> );
        return toggleAlertError( response.errors );
      }

      return history.push( `/configurations/users` )
    } )
  }

  render() {
    const { externalAuthenticationRequired } = this.props;

    return (
      <div className="box box-default">
        <div className="box-header d-flex justify-content-between">
          <span>{this.entityId ? <Translate id="edit"/> : <Translate id="new"/>} <Translate id="user"/></span>
          {this.state.data.last_login &&
          <span className="font-weight-normal">
            <Translate id="lastLogin"/>: {moment( this.state.data.last_login ).format( 'DD/MM/YYYY h:mm A' )}
          </span>
          }
        </div>
        <div className="box-body">
          <Form
            defaultValues={this.formData || this.state.data}
            onSubmit={this.submitForm}
            validate={( values ) => {
              return {
                name: required( values.name ),
                username: required( values.username ),
                roles: required( values.roles ),
                email: required( values.email ) ? required( values.email ) : email( values.email ),
                password: this.entityId || externalAuthenticationRequired || values.externalAuthentication ? null : required( values.password ),
                passwordConfirmation: this.entityId || externalAuthenticationRequired || values.externalAuthentication ? null
                  : required( values.passwordConfirmation ) ? required( values.passwordConfirmation )
                    : equalTo( values.passwordConfirmation, values.password, 'Password' )
              }
            }}
          >
            {( { submitForm, values } ) =>
              <form onSubmit={submitForm}>
                <div className="row">
                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="name"/></label>
                    <Text field="name" className="form-control"/>
                  </FormGroup>

                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="lastName"/></label>
                    <Text field="lastname" className="form-control"/>
                  </FormGroup>
                </div>

                <div className="row">
                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="id"/></label>
                    <Text field="dni" className="form-control"/>
                  </FormGroup>

                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="gender"/></label>
                    <Select field="gender" className="form-control" placeholder=""
                            options={[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]}/>
                  </FormGroup>
                </div>

                <div className="row">
                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="username"/></label>
                    <Text field="username" className="form-control"/>
                  </FormGroup>

                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="email"/></label>
                    <Text field="email" className="form-control"/>
                  </FormGroup>
                </div>

                <div className="row">
                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="userGroup"/></label>
                    <Select field="userGroup" className="form-control" placeholder=""
                            options={this.state.userGroups}/>
                  </FormGroup>

                  <FormGroup containerClass="col-md-6">
                    <label><Translate id="role"/></label>
                    <Select field="roles" className="form-control" placeholder="" multi simpleValue={false}
                            options={[
                              { id: 'ROLE_OPERATOR', name: 'ROLE_OPERATOR' },
                              { id: 'ROLE_SUPERVISOR', name: 'ROLE_SUPERVISOR' },
                              { id: 'ROLE_ADMIN', name: 'ROLE_ADMIN' },
                              { id: 'ROLE_BROADCAST', name: 'ROLE_BROADCAST' },
                              { id: 'ROLE_REPORT', name: 'ROLE_REPORT' },
                              { id: 'ROLE_TECH_ADMIN', name: 'ROLE_TECH_ADMIN' },
                              { id: 'ROLE_FUNCT_ADMIN', name: 'ROLE_FUNCT_ADMIN' }
                            ]}/>
                  </FormGroup>
                </div>

                {!this.entityId ?
                  <div>
                    <div className="row">
                      <FormGroup containerClass="col-md-2 mt-4">
                        <Checkbox field="enabled" label={<Translate id="active"/>} labelStyle={{ width: 'auto' }}/>
                      </FormGroup>
                      {!externalAuthenticationRequired &&
                      <FormGroup containerClass="col-md-4 mt-4">
                        <Checkbox field="externalAuthentication" label={<Translate id="externalAuthentication"/>}
                                  labelStyle={{ width: 'auto' }}/>
                      </FormGroup>
                      }
                      {( !externalAuthenticationRequired && ( values && !values.externalAuthentication ) ) &&
                      <FormGroup containerClass="col-md-6">
                        <label><Translate id="password"/></label>
                        <Text field="password" className="form-control" type="password"/>
                      </FormGroup>
                      }
                    </div>
                    {( !externalAuthenticationRequired && ( values && !values.externalAuthentication ) ) &&
                    <div className="row">
                      <div className="col-md-6"/>
                      <FormGroup containerClass="col-md-6">
                        <label><Translate id="passwordConfirmation"/></label>
                        <Text field="passwordConfirmation" className="form-control" type="password"/>
                      </FormGroup>
                    </div>
                    }
                  </div> :
                  <div className="row">
                    <FormGroup containerClass="col-md-2">
                      <Checkbox field="enabled" label={<Translate id="active"/>} labelStyle={{ width: 'auto' }}/>
                    </FormGroup>

                    {!externalAuthenticationRequired &&
                    <FormGroup containerClass="col-md-4">
                      <Checkbox field="externalAuthentication" label={<Translate id="externalAuthentication"/>}
                                labelStyle={{ width: 'auto' }}/>
                    </FormGroup>
                    }
                  </div>
                }

                <div className="text-right mt-4">
                  <Link to="/configurations/users">
                    <button className="btn btn-outline-secondary px-4 mr-2">
                      <Translate id="cancel"/>
                    </button>
                  </Link>
                  <button type="submit" className="btn btn-info px-4"><Translate id="save"/></button>
                </div>
              </form>
            }
          </Form>
        </div>
      </div>
    )
  }
}

const mapStateToProps = ( state ) => ({
  externalAuthenticationRequired: state.settings.externalAuthenticationRequired,
});

const mapDispatchToProps = dispatch => ({
  toggleAlertError: ( message ) => {
    dispatch( toggleAlertError( message ) );
  },
  toggleLoading: ( isLoading ) => {
    dispatch( toggleLoading( isLoading ) );
  },
});

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps
)( UserEdit );



// WEBPACK FOOTER //
// ./routes/app/routes/configuration/users/edit/index.js